import {strCode} from '../consts/strCode';
import {t} from '../services/localization/localizationService';
import {selectLanguageCode} from '../state/selectors/session';
import {getState} from '../state/store';
import stringUtils from './stringUtils';

const getFormatDate = (date) => {
    const day = date.getDate();
    const month = formatMonthShort(date);
    const monthNumeric = formatMonthNumeric(date);
    const year = date.getFullYear();

    return {
        day,
        month,
        monthNumeric,
        year,
        dateString: `${day} ${month} ${year}`,
    };
};

const formatDate = (options, date) => {
    const languageCode = selectLanguageCode(getState());
    const formatter = new Intl.DateTimeFormat(languageCode, options).format(date);

    return stringUtils.capitalizeFirstLetter(formatter);
};

const formatMonthLong = (date) => formatDate({month: 'long'}, date);
const formatMonthShort = (date) => formatDate({month: 'short'}, date);
const formatMonthDay = (date) => formatDate({month: 'long', day: 'numeric'}, date);
const formatMonthNumeric = (date) => formatDate({month: 'numeric'}, date);
const formatWeekDayLong = (date) => formatDate({weekday: 'long'}, date);
const formatWeekDayShort = (date) => formatDate({weekday: 'short'}, date);
const formatHour12 = (date) => formatDate({hour: 'numeric', hour12: true}, date);
const formatHour24 = (date) => formatDate({hour: '2-digit', minute: '2-digit', hour12: false}, date);

const parseDateString = (dateString, isUtcTime = false) => {
    const [day, month, yearAndTime] = dateString.split('/');
    const [year, time] = yearAndTime.split(' ');
    const [hours, minutes] = time.split(':');

    if (isUtcTime) {
        const utcDate = new Date(year, month - 1, day, hours, minutes);
        const timezoneOffset = new Date().getTimezoneOffset();

        utcDate.setMinutes(utcDate.getMinutes() - timezoneOffset);

        return utcDate;
    }

    return new Date(year, month - 1, day, hours, minutes);
};

const getTimeAgo = ({dateString, isUtcTime = false}) => {
    let value;
    const languageCode = selectLanguageCode(getState());
    const diff = Math.floor((new Date().getTime() - parseDateString(dateString, isUtcTime).getTime()) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    const rtf = new Intl.RelativeTimeFormat(languageCode, {numeric: 'auto'});

    if (years > 0) {
        value = rtf.format(0 - years, 'year');
    } else if (months > 0) {
        value = rtf.format(0 - months, 'month');
    } else if (days > 0) {
        value = rtf.format(0 - days, 'day');
    } else if (hours > 0) {
        value = rtf.format(0 - hours, 'hour');
    } else if (minutes > 0) {
        value = rtf.format(0 - minutes, 'minute');
    } else {
        value = rtf.format(0 - diff, 'second');
    }
    return value;
};

const formatDayWithSuffix = (day) => {
    if (day > 3 && day < 21) return `${day}${t(strCode.DATE_SUFFIX_TH)}`;
    switch (day % 10) {
        case 1:
            return `${day}${t(strCode.DATE_SUFFIX_ST)}`;
        case 2:
            return `${day}${t(strCode.DATE_SUFFIX_ND)}`;
        case 3:
            return `${day}${t(strCode.DATE_SUFFIX_RD)}`;
        default:
            return `${day}${t(strCode.DATE_SUFFIX_TH)}`;
    }
};

const getMondayDate = (date) => {
    const monday = new Date(date);
    const day = monday.getDay() || 7;

    monday.setDate(monday.getDate() - day + 1);
    monday.setHours(0, 0, 0, 0);

    return monday;
};

const formatMonthWithSuffix = (date, formatMonthFn) => {
    const dayDate = new Date(date);

    return `${formatMonthFn(dayDate)} ${formatDayWithSuffix(dayDate.getDate())}`;
};

const formatMonthLongWithSuffix = (date) => formatMonthWithSuffix(date, formatMonthLong);

const formatMonthShortWithSuffix = (date) => formatMonthWithSuffix(date, formatMonthShort);

const getTimeAgoFromTimestamp = (timestamp) => {
    const languageCode = selectLanguageCode(getState());
    const diff = Math.floor((Date.now() - timestamp) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);
    const rtf = new Intl.RelativeTimeFormat(languageCode, {numeric: 'auto'});

    if (years > 0) return rtf.format(-years, 'year');
    if (months > 0) return rtf.format(-months, 'month');
    if (weeks > 0) return rtf.format(-weeks, 'week');
    if (days > 0) return rtf.format(-days, 'day');
    if (hours > 0) return rtf.format(-hours, 'hour');
    if (minutes > 0) return rtf.format(-minutes, 'minute');

    return rtf.format(0, 'second');
};

const getIsLessThanMinuteAgo = (timestamp) => {
    const diff = Math.floor((Date.now() - timestamp) / 1000);
    const minutes = Math.floor(diff / 60);

    return minutes < 1;
};

export default {
    getFormatDate,
    formatMonthLong,
    parseDateString,
    formatMonthShort,
    formatMonthDay,
    formatWeekDayLong,
    formatWeekDayShort,
    formatHour12,
    formatHour24,
    getTimeAgo,
    formatDayWithSuffix,
    getMondayDate,
    formatMonthShortWithSuffix,
    formatMonthLongWithSuffix,
    getTimeAgoFromTimestamp,
    getIsLessThanMinuteAgo,
};
